import { ComponentProps, FC, useEffect } from "react";
import { Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import { Controller, useFormContext } from "react-hook-form";
import styled, { css } from "styled-components";
// @ts-ignore
import { SelectChevronDown } from "../icons.jsx";
// @ts-ignore
import { CustomFloatingLabel } from "./customfloatinglabel.jsx";
// @ts-ignore
import { fontFamily } from "../grid.js";

const FormSelectWrapper = styled("div").attrs({
  className: "FormSelectWrapper",
})<{ active: boolean }>`
  .react-select__control {
    padding: 9px 10px;
    color: #333333;
    background-color: #f0f3f7;
    border-bottom: 1px solid #333333;
    border-radius: 4px 4px 0 0;

    font:
      400 13px/20px ${fontFamily},
      "Segoe UI",
      sans-serif;

    border-top: 0;
    border-left: 0;
    border-right: 0;
    background-size: 12px 6px;
    box-shadow: none;

    &:focus,
    &:hover {
      background-color: #f0f3f7;
      border-bottom: 1px solid #333333;
    }

    ${(props) =>
      props.active &&
      css`
        background-color: rgba(230, 243, 254, 1);
      `};

    .react-select__multi-value {
      padding: 0 0 0 0.5rem;
      height: 1.2rem;
      background: #5952e0;
      border: 0;
      border-radius: 1.2rem;
    }

    .react-select__multi-value__label {
      padding: 0;
      color: #ffffff;
      font-size: 0.6rem;
      line-height: 1.2rem;
    }

    .react-select__multi-value__remove {
      width: 1.2rem;
      height: 1.2rem;
      background-color: #ffffff;
      border-radius: 1.2rem;
      border: 1px solid #5952e0;
      margin-left: 0.5rem;
      background-image: url("data:image/svg+xml,%3Csvg width='11' height='12' viewBox='0 0 11 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cmask id='mask0_428_113' style='mask-type:alpha' maskUnits='userSpaceOnUse' x='0' y='0' width='11' height='11'%3E%3Crect y='0.5' width='11' height='10.2545' fill='%23D9D9D9'/%3E%3C/mask%3E%3Cg mask='url(%23mask0_428_113)'%3E%3C/g%3E%3Cmask id='mask1_428_113' style='mask-type:alpha' maskUnits='userSpaceOnUse' x='2' y='2' width='7' height='8'%3E%3Cmask id='mask2_428_113' style='mask-type:alpha' maskUnits='userSpaceOnUse' x='0' y='0' width='41' height='41'%3E%3Crect x='0.393555' y='0.314453' width='40' height='40' fill='%23D9D9D9'/%3E%3C/mask%3E%3Cg mask='url(%23mask2_428_113)'%3E%3Cpath d='M2.98802 9.10377L2.45337 8.56913L5.07343 5.94895L2.45337 3.32877L2.98802 2.79413L5.60819 5.41419L8.22837 2.79413L8.76302 3.32877L6.14295 5.94895L8.76302 8.56913L8.22837 9.10377L5.60819 6.48371L2.98802 9.10377Z' fill='%231C1B1F'/%3E%3C/g%3E%3C/mask%3E%3Cg mask='url(%23mask1_428_113)'%3E%3Crect width='40' height='40' transform='translate(-14 -13.0461)' fill='%23003A78'/%3E%3Crect x='-14' y='-13.0461' width='40' height='40' fill='%23003A78'/%3E%3C/g%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: 50%;

      svg {
        display: none;
      }
    }
  }
`;

interface FormPersonSelectSupervisorProps {
  disabled?: boolean;
  label?: string;
  required?: boolean;
  customErrorMessage?: string;
  name: string;
  formGroupProps?: ComponentProps<typeof Form.Group>;
  additionalClassnames?: string;
  options: Array<{ label: string; value: string }>;
}

export const FormPersonSelectSupervisor: FC<FormPersonSelectSupervisorProps> = ({
  disabled,
  label,
  required,
  customErrorMessage,
  name,
  formGroupProps,
  additionalClassnames,
  options,
}) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required }}
      render={({
        field: { value, onChange, disabled: globalDisabled, onBlur, ref },
        fieldState: { error, isDirty },
      }) => {
        const errorType = error?.type;

        useEffect(() => {
          if (value && typeof onChange === "function") {
            if (!isDirty) onChange({ value }); // manually trigger the onChange
          }
        }, [value, onChange]);

        const helperText = () => {
          if (errorType === "required" || errorType === undefined) {
            return t("FieldRequired", "Kenttä on pakollinen");
          }
          // @ts-ignore
          return errorType && customErrorMessage?.[errorType] ? customErrorMessage[errorType] : error?.message;
        };

        return (
          <Form.Group
            required={required}
            as={Col}
            controlId={name}
            className={`d-flex flex-column mb-2 ${additionalClassnames ?? ""}`}
            {...formGroupProps}
          >
            <FormSelectWrapper active={isDirty}>
              <CustomFloatingLabel active={isDirty.toString()} label={`${label} ${required ? " *" : ""}`}>
                <ReactSelect
                  inputId={name}
                  name={name}
                  options={options}
                  onChange={onChange}
                  placeholder={t("Select", "Valitse") + "…"}
                  noOptionsMessage={() => t("NoOptions", "Ei valintoja")}
                  value={value.value}
                  isDisabled={disabled || globalDisabled}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  components={{
                    DropdownIndicator: SelectChevronDown,
                    IndicatorSeparator: null,
                    ClearIndicator: undefined,
                  }}
                  isClearable
                  isMulti
                />
              </CustomFloatingLabel>
            </FormSelectWrapper>
            {error && (
              <Form.Control.Feedback type="invalid" className="d-block mb-1">
                {helperText()}.
              </Form.Control.Feedback>
            )}
          </Form.Group>
        );
      }}
    />
  );
};
